import { useEffect, useRef, useState } from 'react'
import { Sparkles, Loader2, X } from 'lucide-react'
import { Button } from '../lib/shadcn/button'
import { Label } from '../lib/shadcn/label'
import { Textarea } from '../lib/shadcn/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../lib/shadcn/select'
import { toast } from '../lib/shadcn/sonner'
import { useGenerateEmailContent } from '../hooks/backend/crm' 

export interface GeneratedEmail {
  subject: string
  body: string
}



interface AiEmailAssistantProps {
  recipientName?: string
  /** Current draft body, passed so the AI can improve/build on it. */
  existingBody?: string
  onGenerated: (result: GeneratedEmail) => void
}

const TONES = ['Friendly', 'Professional', 'Concise', 'Enthusiastic', 'Formal', 'Casual']

// A self-contained "Write with AI" control. The panel is rendered inline within
// the surrounding DOM (NOT a portal) so it works correctly inside a modal Dialog
// — a portaled popover gets its focus stolen by the Dialog's focus trap.
export default function AiEmailAssistant({ recipientName, existingBody, onGenerated }: AiEmailAssistantProps) {
  const generate = useGenerateEmailContent()
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [tone, setTone] = useState('Friendly')
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        // Ignore clicks inside the Select's portaled dropdown.
        const target = e.target as HTMLElement
        if (target.closest('[role="listbox"], [data-radix-popper-content-wrapper]')) return
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Describe what the email should say')
      return
    }
    try {
      const result = (await generate.trigger({
        prompt: prompt.trim(),
        tone,
        ...(recipientName ? { recipient_name: recipientName } : {}),
        ...(existingBody && existingBody.trim() ? { existing_body: existingBody } : {}),
      })) as GeneratedEmail
      onGenerated(result)
      toast.success('Draft generated')
      setOpen(false)
      setPrompt('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate email')
    }
  }

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 h-7"
        onClick={() => setOpen(o => !o)}
      >
        <Sparkles className="h-3.5 w-3.5" />
        Write with AI
      </Button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-80 space-y-3 rounded-md border bg-popover p-3 text-popover-foreground shadow-md">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Write with AI</p>
              <p className="text-xs text-muted-foreground">
                Describe the email and AI drafts the subject and body.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-prompt" className="text-xs">What should it say?</Label>
            <Textarea
              id="ai-prompt"
              autoFocus
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={3}
              placeholder="e.g. Follow up on last week's tasting and ask if they want to place a wholesale order"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            className="w-full gap-1.5"
            size="sm"
            onClick={() => void handleGenerate()}
            disabled={generate.loading}
          >
            {generate.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generate.loading ? 'Generating…' : existingBody && existingBody.trim() ? 'Rewrite draft' : 'Generate'}
          </Button>
        </div>
      )}
    </div>
  )
}
